import React from "react";
import { Shield, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { ImageWithFallback } from "./figma/ImageWithFallback";

// Pest icons for MAIN services
import { FaBug } from "react-icons/fa6";
import { GiInsectJaws, GiRat } from "react-icons/gi";

// Pest icons for ADDITIONAL services
import { GiAnt, GiBee, GiTick } from "react-icons/gi";
import { FaSpider } from "react-icons/fa";

// Simple compatible icon type
type SvgIcon = React.ComponentType<{ className?: string; size?: number | string }>;

export function ServicesPage() {
  const services: {
    title: string;
    description: string;
    image: string;
    icon: SvgIcon;
    features: string[];
    process: string[];
  }[] = [
    {
      title: "Bedbug Treatment",
      description:
          "Complete elimination of bedbugs using advanced treatment and safe chemical applications.",
      image: "/BedBug.png",
      icon: FaBug as SvgIcon,
      features: [
        "Follow-up inspections included",
        "Safe for mattresses and furniture",
        "Safe for kids and pets",
      ],
      process: [
        "Thorough inspection of affected areas",
        "Apprehend biopesticide treatment & targeted chemical application",
        "Follow-up inspections if necessary",
        "Prevention advice and monitoring",
      ],
    },
    {
      title: "Cockroach Control",
      description:
          "Effective cockroach extermination and prevention using targeted baits and barrier treatments.",
      image: "/Cockroach.png",
      icon: GiInsectJaws as SvgIcon,
      features: [
        "Gel bait applications",
        "Crack and crevice treatment",
        "Long-lasting protection",
      ],
      process: [
        "Identification of cockroach species",
        "Strategic bait placement",
        "Sealing of entry points",
        "Regular monitoring and maintenance",
      ],
    },
    {
      title: "Rodent Control",
      description:
          "Comprehensive rodent removal and exclusion services to keep mice and rats out permanently.",
      image: "/Rodent.png",
      icon: GiRat as SvgIcon,
      features: [
        "Humane trapping methods",
        "Entry point sealing",
        "Sanitation guidance",
      ],
      process: [
        "Property inspection and assessment",
        "Strategic trap placement",
        "Exclusion work to prevent re-entry",
        "Cleanup and sanitation recommendations",
      ],
    },
  ];

  const additionalServices: { title: string; icon: SvgIcon }[] = [
    { title: "Ant Control", icon: GiAnt as SvgIcon },
    { title: "Spider Treatment", icon: FaSpider as SvgIcon },
    { title: "Wasp & Bee Removal", icon: GiBee as SvgIcon },
    { title: "Flea & Tick Control", icon: GiTick as SvgIcon },
  ];

  return (
      <div className="min-h-screen">
        <Helmet>
          <title>Pest Control Services | BugBuddies Pest Solutions</title>
          <meta name="description" content="Comprehensive pest control services including bedbug, cockroach, and rodent treatments across Saskatchewan." />
          <link rel="canonical" href="https://bugbuddies.ca/services" />
        </Helmet>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#075123] to-[#05401A] text-white py-16 m-[0px]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-[21px] mt-[10px]">
              Our Pest Control Services
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto mb-8">
              Professional, effective, and safe pest control
              solutions tailored to your specific needs. We
              specialize in treating the most common household
              pests.
            </p>
          </div>
        </section>

        {/* Main Services */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                    <Card key={index} className="h-full flex flex-col">
                      <div className="relative h-48 rounded-t-lg bg-white-100 flex items-center justify-center">
                        <ImageWithFallback
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-contain"
                        />
                      </div>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Icon className="h-6 w-6 text-[#075123]" />
                          {service.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-grow">
                        <p className="text-gray-600 mb-6">
                          {service.description}
                        </p>

                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-3">
                            Key Features:
                          </h4>
                          <ul className="space-y-2">
                            {service.features.map((feature, featureIndex) => (
                                <li
                                    key={featureIndex}
                                    className="flex items-center gap-2"
                                >
                                  <CheckCircle className="h-4 w-4 text-[#075123] flex-shrink-0" />
                                  <span className="text-sm text-gray-600">
                              {feature}
                            </span>
                                </li>
                            ))}
                          </ul>
                        </div>

                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-3">
                            Our Process:
                          </h4>
                          <ol className="space-y-2">
                            {service.process.map((step, stepIndex) => (
                                <li
                                    key={stepIndex}
                                    className="flex items-start gap-2"
                                >
                            <span className="flex-shrink-0 w-5 h-5 bg-[#075123] text-white text-xs rounded-full flex items-center justify-center mt-0.5">
                              {stepIndex + 1}
                            </span>
                                  <span className="text-sm text-gray-600">
                              {step}
                            </span>
                                </li>
                            ))}
                          </ol>
                        </div>

                        <Button
                            className="w-full bg-[#075123] hover:bg-[#05401A] text-white"
                            asChild
                        >
                          <Link to="/contact">Get Free Inspection for {service.title}</Link>
                        </Button>
                      </CardContent>
                    </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Additional Services */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Additional Services
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We also provide treatment for many other common
                pests. Contact us for a consultation.
              </p>
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 justify-items-center">
              {additionalServices.map(({ title, icon: Icon }, index) => (
                  <Card key={index} className="w-full max-w-[220px] text-center p-4">
                    <CardContent className="pt-4">
                      <Icon className="h-8 w-8 text-[#075123] mx-auto mb-2" size={32} />
                      <p className="text-sm font-medium text-gray-900">{title}</p>
                    </CardContent>
                  </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Guarantee Section */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <Shield className="h-16 w-16 text-blue-600 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Service Guarantee
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              We stand behind every service with our 100% satisfaction
              guarantee.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#075123] text-white">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Eliminate Your Pest Problem?
            </h2>
            <p className="text-xl mb-6 text-green-100">
              Don't wait for pests to multiply. Our expert team is
              ready to provide you with a customized solution that
              works.
            </p>
            <div className="bg-[#05401A] rounded-lg p-6 mb-8 inline-block">
              <p className="text-lg font-semibold mb-2">
                <a href="tel:3062017883" className="hover:text-yellow-300">
                  📞 Call Now: (306) 201-7883
                </a>
              </p>
              <p className="text-green-100 mb-2 font-bold">
                To get a quote
              </p>
              <p className="text-lg font-semibold mb-2">
                <a href="tel:3065371423" className="hover:text-yellow-300">
                  📞 Call Now: (306) 537-1423
                </a>
              </p>
            </div>
            <div>
              <Button
                  size="lg"
                  className="bg-white text-[#075123] hover:bg-gray-100"
                  asChild
              >
                <Link to="/contact">Schedule Free Visual Inspection</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
  );
}
